import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@mantine/core";
import type { ReactNode } from "react";
import { usePrefillFile, type Prefill } from "../../lib/usePrefillFile";
import { useAsyncAction } from "../../lib/useAsyncAction";
import { ErrorState, FileDrop, GENERATOR_VIEWS, LoadingState, Page, ViewSwitch } from "../ui";

interface GenerateFormProps {
  /** Tab label under the "Generate" title. */
  description: string;
  submitLabel: string;
  submitIcon: ReactNode;
  busyMessage: string;
  /** Runs the generation for the chosen file; errors are shown in the form. */
  onSubmit: (file: File) => Promise<void>;
  /** Called when the picked file changes, so the page can drop a stale result. */
  onFileChange?: () => void;
  /** Form fields. */
  children: ReactNode;
  /** Rendered under the form once there is something to show. */
  result?: ReactNode;
}

/**
 * The shared shell of every Generate page: file picker (with the optional prefill handed over in
 * router state by a detail page), the fields, the submit button and the loading/error states.
 */
export default function GenerateForm({
  description,
  submitLabel,
  submitIcon,
  busyMessage,
  onSubmit,
  onFileChange,
  children,
  result,
}: GenerateFormProps) {
  const location = useLocation();
  const prefill = (location.state as { prefill?: Prefill } | null)?.prefill ?? null;

  const [file, setFile] = useState<File | null>(null);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const submit = useAsyncAction(onSubmit);

  function handleFileChange(f: File | null) {
    setFile(f);
    setPrefillError(null);
    submit.clearError();
    onFileChange?.();
  }

  const prefillLoading = usePrefillFile(prefill, { onFile: handleFileChange, onError: setPrefillError });
  const busy = submit.busy || prefillLoading;
  const error = prefillError ?? submit.error;

  return (
    <Page title="Generate" description={description} actions={<ViewSwitch base="/generate" options={GENERATOR_VIEWS} />} maw={640}>
      <FileDrop value={file} onChange={handleFileChange} disabled={busy} />

      {children}

      <Button leftSection={submitIcon} onClick={() => file && void submit.run(file)} disabled={!file || busy} loading={submit.busy}>
        {submitLabel}
      </Button>

      {prefillLoading && <LoadingState h={120} message="Loading original image…" />}
      {submit.busy && <LoadingState h={120} message={busyMessage} />}
      {error && <ErrorState message={error} />}

      {result}
    </Page>
  );
}
